import { Clock, ShieldCheck, Users, BadgeDollarSign, PhoneCall } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, SUPPORT_IMAGE } from "@/lib/site-content";

const PILLARS = [
  {
    icon: Clock,
    title: "Same-day setup",
    desc: "Most start, stop and transfer requests close the same day you call.",
  },
  {
    icon: ShieldCheck,
    title: "Licensed specialists",
    desc: "Certified utility coordinators handle electric, gas and water across the U.S.",
  },
  {
    icon: Users,
    title: "Real humans",
    desc: "No phone trees. No bots. Talk to a real specialist on the first ring.",
  },
  {
    icon: BadgeDollarSign,
    title: "Transparent pricing",
    desc: "Clear setup costs and zero hidden fees — explained before any change.",
  },
];

export default function WhyUs() {
  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      className="bg-zinc-900 text-white py-20 sm:py-28 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <div className="relative">
            <img
              src={SUPPORT_IMAGE}
              alt="Specpay Solutions specialist on phone"
              className="w-full h-full object-cover aspect-[4/5]"
            />
            <div className="absolute -bottom-6 -right-6 hidden sm:block bg-[#FF4500] text-white p-6 max-w-xs">
              <p className="label-eyebrow text-white/80 mb-2">Average wait</p>
              <p className="font-display font-black text-4xl tracking-tight">
                &lt; 30 sec
              </p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 order-1 lg:order-2">
          <p className="label-eyebrow text-zinc-400 mb-4">Why Specpay</p>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tighter">
            We do the calling,
            <br />
            <span className="text-[#FF4500]">waiting and paperwork.</span>
          </h2>
          <p className="mt-6 text-zinc-300 text-base sm:text-lg leading-relaxed max-w-2xl">
            Switching utility providers, moving cities or just trying to pay a
            bill shouldn't take an afternoon. We handle the whole process so
            you don't have to.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            {PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  data-testid={`pillar-${p.title.toLowerCase().replace(/\s/g, "-")}`}
                  className="border border-zinc-700 hover:border-[#FF4500] p-5 transition-colors"
                >
                  <div className="w-10 h-10 bg-[#FF4500] flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-white" strokeWidth={2.5} />
                  </div>
                  <h3 className="font-display font-bold text-lg tracking-tight">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-zinc-400 text-sm leading-relaxed">
                    {p.desc}
                  </p>
                </div>
              );
            })}
          </div>

          <a
            href={PHONE_TEL}
            data-testid="why-us-call-button"
            className="mt-10 inline-flex items-center gap-3 bg-[#FF4500] hover:bg-[#E63E00] text-white font-display font-black px-8 py-5 text-lg transition-all active:scale-[0.97]"
          >
            <PhoneCall className="w-5 h-5" strokeWidth={2.7} />
            Call {PHONE_DISPLAY}
          </a>
        </div>
      </div>
    </section>
  );
}
